import React, { memo, useCallback, useState } from 'react'
import { ICarrierAutosuggestAutocompleteProps } from 'apps/marketplace/components/CarrierAutosuggest/CarrierAutosuggest'
import { ICarrierResultFragment } from 'apps/marketplace/components/CarrierAutosuggest/graphql/CarrierResultFragment'
import ControlledCarrierAutosuggest, {
  IControlledCarrierAutosuggestProps,
} from 'apps/marketplace/components/ControlledCarrierAutosuggest'
import { useModal } from 'utils/hooks'
import CreateCarrierConfirmationModal from './CreateCarrierConfirmationModal'

const CONFIRM_CREATE = 'confirmCreate'

export interface ICarrierAutosuggestWithCreateAutocompleteProps
  extends Omit<ICarrierAutosuggestAutocompleteProps, 'freeSolo' | 'clearOnBlur'> {}

export interface ICarrierAutosuggestWithCreateProps
  extends Omit<
    IControlledCarrierAutosuggestProps,
    'enableCreateCarrier' | 'onCreateCarrier' | 'AutocompleteProps'
  > {
  AutocompleteProps?: ICarrierAutosuggestWithCreateAutocompleteProps
  onCreate: (newCarrier: ICarrierResultFragment) => void
}

const CarrierAutosuggestWithCreate = memo<ICarrierAutosuggestWithCreateProps>(
  ({ AutocompleteProps, onCreate, ...props }) => {
    // Text input for autosuggest field
    const [inputValue, setInputValue] = useState('')

    // Carrier Number
    const [carrierNumber, setCarrierNumber] = useState('')

    // Modal State
    const { closeModal, isOpen, openModal } = useModal()
    const isConfirmationModalOpen = isOpen(CONFIRM_CREATE)

    const handleOpenCreateCarrierModal = useCallback(
      (carrierNumber: string) => {
        setCarrierNumber(carrierNumber)
        openModal(CONFIRM_CREATE)()
      },
      [setCarrierNumber, openModal]
    )

    const handleCreationSuccess = useCallback(
      (newCarrier: ICarrierResultFragment) => {
        setInputValue(``)
        onCreate(newCarrier)
        closeModal()
      },
      [closeModal, onCreate]
    )

    const handleCancel = useCallback(() => {
      closeModal()
      setInputValue(``)
    }, [closeModal])

    return (
      <div>
        <ControlledCarrierAutosuggest
          {...props}
          AutocompleteProps={{ ...AutocompleteProps, freeSolo: true, clearOnBlur: false }}
          enableCreateCarrier
          onCreateCarrier={handleOpenCreateCarrierModal}
          onClear={handleCancel}
          inputValue={inputValue}
          onInputValueChange={setInputValue}
        />
        {isConfirmationModalOpen && (
          <CreateCarrierConfirmationModal
            carrierNumber={carrierNumber}
            open={isConfirmationModalOpen}
            onClose={handleCancel}
            onSuccess={handleCreationSuccess}
          />
        )}
      </div>
    )
  }
)

export default CarrierAutosuggestWithCreate
